"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ArrowRight, HelpCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatShortcutsInText, useIsMacLike } from "@/lib/platformShortcuts";
import type { WalkthroughPlacement, WalkthroughProps } from "./types";

// Z-stack contract, bottom-right corner, from the floor up:
//   WrenchAssistant launcher: z-[80], bottom-5  (src/components/wrench/WrenchAssistant.tsx)
//   Tour re-trigger pill:     z-[79], bottom-[5.5rem]
//   Tour dimmer SVG:          z-[90]
//   Tour tooltip:             z-[91]
// The two fixed controls are one vertical stack and are offset rather than
// layered, so none of them overlaps another. Anything added here takes the next
// slot up.
const SPOTLIGHT_PADDING = 8;
const SPOTLIGHT_RADIUS = 14;
const TOOLTIP_GAP = 14;
const TOOLTIP_MIN_WIDTH = 240;
const TOOLTIP_MAX_WIDTH = 360;
const REPOSITION_INTERVAL_MS = 250;
const ESTIMATED_TOOLTIP_HEIGHT = 200;
const VIEWPORT_MARGIN = 16;

/**
 * One flag for every tool, not one per tour: the pill's own close button means
 * "stop showing me this button", and somebody who says that on one tool means
 * it on all of them. Hides only the pill — first-visit auto-start is still
 * governed by each tour's own `storageKey`.
 */
const PILL_HIDDEN_KEY = "workshop_tour_pill_hidden_v1";

type Rect = { x: number; y: number; width: number; height: number };
type Position = { top: number; left: number; width: number; placement: WalkthroughPlacement };

function readPersisted(key: string): boolean {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writePersisted(key: string): void {
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    // private mode etc.
  }
}

export function getTargetRect(selector: string | undefined): Rect | null {
  if (!selector || typeof document === "undefined") return null;
  if (selector.includes(" >> ")) {
    const [frameSelector, targetSelector] = selector.split(" >> ");
    try {
      const frame = document.querySelector<HTMLIFrameElement>(frameSelector);
      const target = frame?.contentDocument?.querySelector(targetSelector);
      if (!frame || !target) return null;
      const outer = frame.getBoundingClientRect();
      const inner = target.getBoundingClientRect();
      if (!inner.width || !inner.height) return null;
      return { x: outer.x + inner.x, y: outer.y + inner.y, width: inner.width, height: inner.height };
    } catch { return null; }
  }
  let el: Element | null = null;
  try {
    el = document.querySelector(selector);
  } catch {
    return null;
  }
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (rect.width > 0 || rect.height > 0) {
    return { x: rect.x, y: rect.y, width: rect.width, height: rect.height };
  }
  // Element has no layout box (e.g. wrapped with `display: contents`). Fall back to
  // the union rect of its children via the Range API so the spotlight still works.
  try {
    const range = document.createRange();
    range.selectNodeContents(el);
    const r = range.getBoundingClientRect();
    range.detach?.();
    if (r.width > 0 || r.height > 0) {
      return { x: r.x, y: r.y, width: r.width, height: r.height };
    }
  } catch {
    // ignore
  }
  return null;
}

function clampTooltipWidth(viewportWidth: number): number {
  return Math.max(TOOLTIP_MIN_WIDTH, Math.min(TOOLTIP_MAX_WIDTH, viewportWidth - 32));
}

function resolvePosition(
  rect: Rect | null,
  preferred: WalkthroughPlacement,
  vw: number,
  vh: number,
  estimatedHeight: number,
): Position {
  const width = clampTooltipWidth(vw);

  const centerPos = (): Position => ({
    top: Math.max(VIEWPORT_MARGIN, vh / 2 - estimatedHeight / 2),
    left: Math.max(VIEWPORT_MARGIN, vw / 2 - width / 2),
    width,
    placement: "center",
  });

  if (!rect || preferred === "center") return centerPos();

  const opposites: Record<Exclude<WalkthroughPlacement, "center">, WalkthroughPlacement> = {
    top: "bottom",
    bottom: "top",
    left: "right",
    right: "left",
  };
  const cross: WalkthroughPlacement[] =
    preferred === "top" || preferred === "bottom" ? ["right", "left"] : ["bottom", "top"];
  const candidates: WalkthroughPlacement[] = [preferred, opposites[preferred], ...cross];

  for (const p of candidates) {
    let top = 0;
    let left = 0;
    if (p === "top") {
      top = rect.y - estimatedHeight - TOOLTIP_GAP;
      left = rect.x + rect.width / 2 - width / 2;
    } else if (p === "bottom") {
      top = rect.y + rect.height + TOOLTIP_GAP;
      left = rect.x + rect.width / 2 - width / 2;
    } else if (p === "left") {
      top = rect.y + rect.height / 2 - estimatedHeight / 2;
      left = rect.x - width - TOOLTIP_GAP;
    } else if (p === "right") {
      top = rect.y + rect.height / 2 - estimatedHeight / 2;
      left = rect.x + rect.width + TOOLTIP_GAP;
    } else {
      continue;
    }

    const clampedLeft = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - width - VIEWPORT_MARGIN));
    const clampedTop = Math.max(VIEWPORT_MARGIN, Math.min(top, vh - estimatedHeight - VIEWPORT_MARGIN));

    const xFits = clampedLeft === left;
    const yFits = clampedTop === top;

    // For top/bottom placements, the y-axis is the critical one.
    // For left/right placements, the x-axis is the critical one.
    if ((p === "top" || p === "bottom") && yFits) {
      return { top: clampedTop, left: clampedLeft, width, placement: p };
    }
    if ((p === "left" || p === "right") && xFits) {
      return { top: clampedTop, left: clampedLeft, width, placement: p };
    }
  }

  return centerPos();
}

export function ToolWalkthrough({
  steps,
  storageKey,
  enabled = true,
  onFinish,
  finishLabel,
  openSignal,
  autoStart,
}: WalkthroughProps) {
  const maskId = useId();
  /**
   * Manual mode, decided by the *presence* of `openSignal` and not by its value.
   *
   * A caller that wants to decide when its tour opens has to be able to say so
   * on the first render, before it has anything to open the tour over — and its
   * first value is `0`, which is falsy. So presence is the switch. See the prop's
   * own comment in `types.ts` for why this exists at all; in one line, a tour
   * that opens because a report appeared cannot tell whose report it is.
   */
  const manual = openSignal !== undefined;
  /**
   * Whether the first-visit auto-start and the floating pill exist. Manual mode
   * switches both off by default, which is right for the free tools, where the
   * page is somebody's own `resources/` folder. A creator tool that also wants a
   * sample button to open its tour passes `autoStart` to keep the first-visit
   * behaviour every creator tour relies on; the signal then only adds a door.
   */
  const selfStarting = autoStart ?? !manual;
  const isMacLike = useIsMacLike();
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [position, setPosition] = useState<Position>({
    top: 0,
    left: 0,
    width: TOOLTIP_MAX_WIDTH,
    placement: "center",
  });
  // Tracks whether we've computed at least one position. Until then the tooltip
  // stays opacity-0 so it doesn't visibly fly in from (0,0) on first render.
  const [hasResolved, setHasResolved] = useState(false);
  const [pillHidden, setPillHidden] = useState(false);

  const totalSteps = steps.length;
  const step = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;

  const finish = useCallback(() => {
    setActive(false);
    setStepIndex(0);
    setHasResolved(false);
    writePersisted(storageKey);
    onFinish?.();
  }, [onFinish, storageKey]);

  // Mount flag — guards SSR.
  useEffect(() => {
    setMounted(true);
    setPillHidden(readPersisted(PILL_HIDDEN_KEY));
  }, []);

  // First-visit auto-start, and the only thing the persisted flag decides.
  //
  // It used to decide two things — whether the tour opens by itself, and
  // whether the re-trigger pill exists at all — and the second one made a
  // finished tour unreplayable for good. Now the flag means exactly "this
  // person has met this tour", which is a reason not to throw it at them
  // unasked and not a reason to refuse them when they ask.
  //
  // Skipped entirely in manual mode: a caller passing `openSignal` has said that
  // the only thing allowed to put a dimmer over this page is a person asking
  // for it, and "unless they have been here before" is not that.
  useEffect(() => {
    if (!mounted || !enabled || totalSteps === 0 || !selfStarting) return;
    if (readPersisted(storageKey)) return;
    setActive(true);
    setStepIndex(0);
  }, [mounted, enabled, storageKey, totalSteps, selfStarting]);

  /**
   * The manual open.
   *
   * The first value seen is recorded and does nothing, because a mount is not a
   * request; every raise after it opens the tour at step one. Deliberately
   * unguarded by the persisted flag — a user who pressed "show me" after
   * finishing the tour once is asking for it again. The floating pill now
   * follows the same rule, so this is no longer the only channel that can
   * replay a finished tour; it is still the only one a caller controls.
   */
  const lastSignal = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (openSignal === undefined) return;
    if (lastSignal.current === undefined || openSignal === lastSignal.current) {
      lastSignal.current = openSignal;
      return;
    }
    lastSignal.current = openSignal;
    setStepIndex(0);
    setHasResolved(false);
    setActive(true);
  }, [openSignal]);

  // Position recalculation: rect + tooltip placement, on rAF after step change/resize/scroll/poll.
  const recompute = useCallback(() => {
    if (typeof window === "undefined" || !step) return;
    const nextRect = getTargetRect(step.selector);
    setRect(nextRect);
    const tooltipHeight = tooltipRef.current?.offsetHeight ?? ESTIMATED_TOOLTIP_HEIGHT;
    setPosition(
      resolvePosition(
        nextRect,
        step.placement ?? "center",
        window.innerWidth,
        window.innerHeight,
        tooltipHeight,
      ),
    );
    setHasResolved(true);
  }, [step]);

  useEffect(() => {
    if (!active || !mounted) return;
    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(recompute);
    };
    schedule();

    // Guarded because a tour is an accessory and may never take its tool down
    // with it. `new ResizeObserver` inside an effect throws where the API is
    // missing — jsdom has none, and neither does a browser old enough to matter
    // — and a throw in a passive effect unmounts the whole tree, so the page
    // this tour was explaining would go blank. Without it the spotlight simply
    // repositions on the scroll, resize and poll listeners below instead.
    const ro =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    ro?.observe(document.body);

    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, { capture: true, passive: true });
    const poll = window.setInterval(schedule, REPOSITION_INTERVAL_MS);

    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, { capture: true } as EventListenerOptions);
      window.clearInterval(poll);
    };
  }, [active, mounted, recompute]);

  // Scroll the target element into view on step change. Existing scroll listeners
  // already keep the spotlight + tooltip aligned while the smooth-scroll animates.
  useEffect(() => {
    if (!active || !mounted) return;
    const sel = step?.selector;
    if (!sel) return;
    let el: Element | null = null;
    try {
      if (sel.includes(" >> ")) {
        const [frameSelector, targetSelector] = sel.split(" >> ");
        el = document.querySelector<HTMLIFrameElement>(frameSelector)?.contentDocument?.querySelector(targetSelector) ?? null;
      } else el = document.querySelector(sel);
    } catch {
      return;
    }
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const targetWindow = el.ownerDocument.defaultView ?? window;
    const offscreen =
      rect.top < 0 ||
      rect.bottom > targetWindow.innerHeight ||
      rect.left < 0 ||
      rect.right > targetWindow.innerWidth;
    if (offscreen) {
      el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    }
  }, [active, mounted, stepIndex, step?.selector]);

  // Keyboard handling.
  useEffect(() => {
    if (!active) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        finish();
        return;
      }
      if (e.key === "ArrowRight" || e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        setStepIndex((i) => {
          if (i >= totalSteps - 1) {
            // last step → finish via microtask so state stays consistent
            queueMicrotask(finish);
            return i;
          }
          return i + 1;
        });
        return;
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        e.stopPropagation();
        setStepIndex((i) => Math.max(0, i - 1));
      }
    };
    window.addEventListener("keydown", handler, { capture: true });
    return () => window.removeEventListener("keydown", handler, { capture: true } as EventListenerOptions);
  }, [active, finish, totalSteps]);

  if (!mounted || typeof document === "undefined") return null;
  if (!enabled || totalSteps === 0) return null;

  const showSpotlight = active && rect && step && step.placement !== "center";
  const spotlightX = showSpotlight && rect ? rect.x - SPOTLIGHT_PADDING : 0;
  const spotlightY = showSpotlight && rect ? rect.y - SPOTLIGHT_PADDING : 0;
  const spotlightW = showSpotlight && rect ? rect.width + SPOTLIGHT_PADDING * 2 : 0;
  const spotlightH = showSpotlight && rect ? rect.height + SPOTLIGHT_PADDING * 2 : 0;

  const overlay = active && step ? (
    <div className="fixed inset-0 z-[90]" aria-modal="true" role="dialog" aria-label={step.title}>
      {/* Dimmer with optional spotlight cutout. Spotlight rect attrs animate via CSS transitions on x/y/width/height. */}
      <svg
        className="absolute inset-0 h-full w-full"
        width="100%"
        height="100%"
        aria-hidden="true"
      >
        <defs>
          <mask id={maskId}>
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {showSpotlight ? (
              <rect
                x={spotlightX}
                y={spotlightY}
                width={spotlightW}
                height={spotlightH}
                rx={SPOTLIGHT_RADIUS}
                ry={SPOTLIGHT_RADIUS}
                fill="black"
                style={{
                  transition:
                    "x 220ms cubic-bezier(0.4,0,0.2,1), y 220ms cubic-bezier(0.4,0,0.2,1), width 220ms cubic-bezier(0.4,0,0.2,1), height 220ms cubic-bezier(0.4,0,0.2,1)",
                }}
              />
            ) : null}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.6)"
          mask={`url(#${maskId})`}
        />
      </svg>

      {/* Tooltip — wrapper transitions on top/left/width; inner content cross-fades on step change via key. */}
      <div
        ref={tooltipRef}
        className={cn(
          "absolute z-[91] rounded-2xl border border-border/70 bg-popover/95 p-5 shadow-[0_30px_60px_hsl(var(--background)/0.55)] backdrop-blur-xl",
          hasResolved ? "opacity-100" : "opacity-0",
        )}
        style={{
          top: position.top,
          left: position.left,
          width: position.width,
          transition: hasResolved
            ? "top 220ms cubic-bezier(0.4,0,0.2,1), left 220ms cubic-bezier(0.4,0,0.2,1), width 220ms cubic-bezier(0.4,0,0.2,1), opacity 180ms ease-out"
            : "opacity 180ms ease-out",
        }}
      >
        <div className="mb-2 flex items-center justify-between">
          <span className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-muted-foreground">
            {`Step ${stepIndex + 1} of ${totalSteps}`}
          </span>
          <button
            type="button"
            onClick={finish}
            aria-label="Close tour"
            className="rounded-md p-1 text-muted-foreground transition hover:bg-muted/40 hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div key={stepIndex} className="animate-in fade-in-0 slide-in-from-bottom-1 duration-200">
          <h2 className="text-lg font-semibold tracking-[-0.01em] text-foreground">{step.title}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">{formatShortcutsInText(step.body, isMacLike)}</p>
        </div>
        {isLast ? (
          // Final step: primary CTA is the focal point. Skip is redundant with Continue
          // (both close + persist), so it's removed. Back becomes a small ghost link below.
          <div className="mt-5 flex flex-col gap-3">
            <Button
              variant="premium"
              size="sm"
              className="w-full whitespace-normal text-center"
              onClick={finish}
            >
              {finishLabel ?? "Got it"}
            </Button>
            {!isFirst ? (
              <button
                type="button"
                onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                className="self-center inline-flex items-center gap-1 text-xs text-muted-foreground transition hover:text-foreground"
              >
                <ArrowLeft className="h-3 w-3" />
                Back
              </button>
            ) : null}
          </div>
        ) : (
          // Non-final steps: Skip on the left, Back + Next grouped on the right.
          <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2">
            <button
              type="button"
              onClick={finish}
              className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-muted-foreground transition hover:text-foreground"
            >
              Skip
            </button>
            <div className="ml-auto flex items-center gap-2">
              {!isFirst ? (
                <Button
                  variant="premiumGhost"
                  size="sm"
                  onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back
                </Button>
              ) : null}
              <Button variant="premium" size="sm" onClick={() => setStepIndex((i) => i + 1)}>
                Next
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  ) : null;

  // No floating pill in manual mode. The caller owns the entry point there —
  // on the four free tools it is the demo button and a permanent control in the
  // intake row (`ShowTourButton`), which is where a tour of a loaded sample has
  // something to point at.
  //
  // Not gated on the persisted flag any more, and that is the fix for a defect
  // the tutorial spec names outright: `finish()` persists dismissal and the
  // pill required the absence of it, so the pill vanished the first time a user
  // closed or completed a tour and there was then **no way to replay it, ever**,
  // on any of the twenty creator tours. A tour is the only tutorial inside the
  // product that sixteen of those tools have, and it was one-shot.
  //
  // The flag still does the job it was written for, one effect above: it stops
  // the tour opening by itself on a second visit. What it should never have
  // controlled is whether a person who asks for the tour again is allowed to
  // have it — which is the same reasoning `openSignal` uses to ignore it.
  const reTrigger = selfStarting && !active && !pillHidden ? (
    <div className="pointer-events-none fixed bottom-[5.5rem] right-5 z-[79] flex items-center gap-1">
      <Button
        type="button"
        variant="premiumOutline"
        size="sm"
        onClick={() => {
          setStepIndex(0);
          setActive(true);
        }}
        className="pointer-events-auto"
      >
        <HelpCircle className="h-4 w-4" />
        Show tour
      </Button>
      <button
        type="button"
        aria-label="Hide the Show tour button on every tool"
        title="Hide on every tool"
        onClick={() => {
          writePersisted(PILL_HIDDEN_KEY);
          setPillHidden(true);
        }}
        className="pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-full border border-border/70 bg-card/95 text-muted-foreground shadow-sm backdrop-blur transition-colors hover:border-primary/40 hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  ) : null;

  return createPortal(
    <>
      {overlay}
      {reTrigger}
    </>,
    document.body,
  );
}

export default ToolWalkthrough;
